/**
 * Brand Lift Survey translation catalogue.
 *
 * Generated by scripts/build-bls-translations.mjs from docs/Translation files.
 * Questions, answers, and survey chrome are joined by workbook ID. {BRAND}
 * stays in the catalogue and is replaced when the survey is rendered.
 */

import catalogue from './translations.json';

const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'yi', 'dv', 'ckb', 'arc']);

export function surveyLocales() {
  return catalogue.locales || [{ id: 'en', label: 'English', rtl: false }];
}

export function isRtlLocale(localeId) {
  const id = String(localeId || '').toLowerCase();
  const known = surveyLocales().find((locale) => locale.id.toLowerCase() === id);
  if (known) return Boolean(known.rtl);
  const primary = id.split('-')[0];
  return RTL_LOCALES.has(primary);
}

export function actionWordsFor(metricId) {
  return [...(catalogue.metrics?.[metricId]?.actionWords || [])];
}

export function englishQuestion(metricId, actionWord) {
  const questions = catalogue.metrics?.[metricId]?.questions?.[String(actionWord || '').toLowerCase()];
  return questions?.full?.en || questions?.short?.en || '';
}

export function englishAnswerList(metricId, variant) {
  const rows = catalogue.metrics?.[metricId]?.answers?.[variant] || [];
  return rows.filter(Boolean).map((row) => row.en).filter(Boolean);
}

export function fillSurveyBrand(value, brand) {
  const source = String(value ?? '');
  const name = brand || '';
  return source
    .replace(/\{BRAND\}/g, name || '{BRAND}')
    .replace(/\[BRAND\]/g, name || '[BRAND]');
}

function answerVariant(metricId, answerCount) {
  const answers = catalogue.metrics?.[metricId]?.answers || {};
  if (String(answerCount) === '3' && answers['3']) return '3';
  if (answers['5'] && (String(answerCount) === '5' || !answers.default)) return '5';
  if (answers.default) return 'default';
  if (answers['5']) return '5';
  return '';
}

function normalizeCopy(value) {
  return String(value || '')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Slice of the catalogue embedded in one survey file.
 * Edited answers stay as typed in every language. Tracking text is separate.
 */
export function buildSurveyPack(options = {}) {
  const metricId = options.metricId || '';
  const metric = catalogue.metrics?.[metricId] || null;
  const action = String(options.actionWord || metric?.actionWords?.[0] || '').toLowerCase();
  const questions = metric?.questions?.[action] || null;
  const variant = answerVariant(metricId, options.answerCount);
  const catalogueAnswers = variant ? (metric?.answers?.[variant] || []).filter(Boolean) : [];
  const typed = (options.answers || []).map((answer) => fillSurveyBrand(answer, options.brand));
  const overrides = typed.map((answer, index) => {
    const expected = catalogueAnswers[index]?.en;
    if (!expected) return answer || null;
    if (normalizeCopy(answer) !== normalizeCopy(fillSurveyBrand(expected, options.brand))) return answer;
    return null;
  });

  return {
    previewLocale: options.previewLocale || null,
    brand: options.brand || '',
    locales: surveyLocales().map((locale) => ({ id: locale.id, rtl: Boolean(locale.rtl) })),
    question: questions
      ? { full: questions.full || {}, short: questions.short || {} }
      : null,
    customQuestion: questions ? '' : String(options.question || ''),
    answers: catalogueAnswers,
    overrides,
    ui: catalogue.ui || {},
  };
}

export function surveyLocaleRuntime(pack) {
  return `
  var BLS_PACK = ${JSON.stringify(pack).replace(/</g, '\\u003c')};
  function blsEscape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function blsFillBrand(text) {
    var brand = BLS_PACK.brand || '';
    return String(text || '')
      .replace(/\\{BRAND\\}/g, brand || '{BRAND}')
      .replace(/\\[BRAND\\]/g, brand || '[BRAND]');
  }
  function blsHighlight(text) {
    var filled = blsFillBrand(text);
    var safe = blsEscape(filled);
    var needle = blsEscape(BLS_PACK.brand || '{BRAND}');
    if (!needle) return safe;
    return safe.split(needle).join('<span class="brand">' + needle + '</span>');
  }
  function blsPick(map, locale) {
    if (!map) return '';
    if (map[locale]) return map[locale];
    var wanted = String(locale || '').toLowerCase();
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i += 1) {
      if (String(keys[i]).toLowerCase() === wanted && map[keys[i]]) return map[keys[i]];
    }
    return map.en || '';
  }
  function blsLocale() {
    if (BLS_PACK.previewLocale) return BLS_PACK.previewLocale;
    var nav = 'en';
    try {
      nav = String(navigator.language || navigator.userLanguage || 'en').toLowerCase().replace(/_/g, '-');
    } catch (e) { nav = 'en'; }
    var ids = [];
    var locales = BLS_PACK.locales || [];
    for (var i = 0; i < locales.length; i += 1) {
      if (locales[i] && locales[i].id && locales[i].id !== 'en') ids.push(locales[i].id);
    }
    ids.sort(function (a, b) { return String(b).length - String(a).length; });
    for (var j = 0; j < ids.length; j += 1) {
      var id = String(ids[j]).toLowerCase();
      if (nav === id || nav.indexOf(id + '-') === 0) return ids[j];
    }
    return 'en';
  }
  function blsLocaleMeta(locale) {
    var locales = BLS_PACK.locales || [];
    var wanted = String(locale || '').toLowerCase();
    for (var i = 0; i < locales.length; i += 1) {
      if (String(locales[i].id).toLowerCase() === wanted) return locales[i];
    }
    return null;
  }
  function blsBox() {
    return document.getElementById('survey');
  }
  function blsIsShort() {
    var el = blsBox();
    return !!(el && el.clientHeight > 0 && el.clientHeight <= 140);
  }
  function blsIsCompact() {
    var el = blsBox();
    return !!(el && el.clientWidth > 0 && el.clientWidth <= 420 && el.clientHeight > 0 && el.clientHeight <= 280);
  }
  function blsQuestionText(locale) {
    if (BLS_PACK.customQuestion) return BLS_PACK.customQuestion;
    var questions = BLS_PACK.question || {};
    var lengthName = blsIsShort() ? 'short' : 'full';
    var text = blsPick(questions[lengthName], locale);
    if (!text && lengthName === 'short') text = blsPick(questions.full, locale);
    return text || '';
  }
  function blsApplyLocale() {
    var locale = blsLocale();
    var meta = blsLocaleMeta(locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = meta && meta.rtl ? 'rtl' : 'ltr';
    var tagline = document.getElementById('tagline');
    if (tagline) {
      var ui = BLS_PACK.ui || {};
      var line = blsIsCompact() ? blsPick(ui.taglineCompact, locale) : blsPick(ui.tagline, locale);
      tagline.textContent = line ? '\\u2014 ' + line : '';
    }
    var question = document.getElementById('question');
    var questionText = blsQuestionText(locale);
    if (question && questionText) question.innerHTML = blsHighlight(questionText);
    var labels = document.querySelectorAll('.bls-label');
    var maps = BLS_PACK.answers || [];
    var overrides = BLS_PACK.overrides || [];
    for (var index = 0; index < labels.length; index += 1) {
      var text = overrides[index] ? overrides[index] : blsPick(maps[index], locale);
      if (!text && maps[index]) text = blsPick(maps[index], 'en');
      if (text) labels[index].textContent = blsFillBrand(text);
    }
    var thanks = document.getElementById('thanks-title');
    if (thanks) {
      var thanksText = blsPick((BLS_PACK.ui || {}).thanks, locale);
      if (thanksText) thanks.textContent = thanksText;
    }
  }
  blsApplyLocale();
  if (window.ResizeObserver) {
    var surveyEl = blsBox();
    if (surveyEl) new ResizeObserver(blsApplyLocale).observe(surveyEl);
  }
`;
}
